import React from 'react';
import { render, cleanup, act } from '@testing-library/react';
import { withWrapper } from '../testWrapper';
import { enableMocking, disableMocking } from '../mockApi';
import { captureComponentSnapshot, createLogCapture } from '../snapshotCapture';

function assert(condition, message) {
  if (!condition) throw new Error(message);
}

async function renderWithMockApi(element, assertions, waitMs = 250) {
  enableMocking();
  let snapshot = null;
  const logCapture = createLogCapture();
  try {
    let result;
    await act(async () => {
      result = render(element);
      await new Promise(r => setTimeout(r, waitMs));
    });
    await assertions(result);
    snapshot = captureComponentSnapshot(result.container);
  } finally {
    cleanup();
    disableMocking();
    logCapture.restore();
  }
  return { snapshot, logs: logCapture.getLogs() };
}

export const featureTests = [
  {
    id: 'feat-projects-1',
    name: 'Projects page renders without crashing (mocked API)',
    run: async () => {
      const Projects = require('../../Pages/Private/Settings/Projects/Projects').default;
      return await renderWithMockApi(
        withWrapper(<Projects />, '/projects'),
        async ({ container }) => {
          assert(container.firstChild !== null, 'Projects page should render something');
        }
      );
    },
  },
  {
    id: 'feat-saved-articles-1',
    name: 'SavedArticlesTable renders article titles',
    run: async () => {
      const SavedArticlesTable = require('../../Components/ArticleBibliography/SavedArticlesTable').default;
      const mockArticles = [
        { id: 1, title: 'Integration Test Article', abstract: 'Test abstract content', authors: [{ name: 'Author A' }], journal_name: 'Science', publication_year: 2023, cited_by_count: 10 },
        { id: 2, title: 'Second Test Article', abstract: 'Another abstract', authors: [{ name: 'Author B' }], journal_name: 'Nature', publication_year: 2022, cited_by_count: 5 },
      ];
      return await renderWithMockApi(
        withWrapper(
          <SavedArticlesTable
            articles={mockArticles}
            loading={false}
            pagination={null}
            onPageChange={() => {}}
            onArticleDeleted={() => {}}
          />
        ),
        async ({ container }) => {
          assert(container.textContent.includes('Integration Test Article'), 'Should show first article title');
          assert(container.textContent.includes('Second Test Article'), 'Should show second article title');
        }
      );
    },
  },
  {
    id: 'feat-project-card-testid-1',
    name: 'ProjectCard has data-testid attribute',
    run: async () => {
      const ProjectCard = require('../../Components/Projects/ProjectCard').default;
      const mockProject = {
        uuid: 'nav-test-uuid',
        name: 'Nav Project',
        description: 'desc',
        completed_steps: ['data_collection'],
      };
      return await renderWithMockApi(
        withWrapper(<ProjectCard project={mockProject} />),
        async ({ container }) => {
          const el = container.querySelector('[data-testid="project-card-nav-test-uuid"]');
          assert(el !== null, 'ProjectCard should have data-testid="project-card-{uuid}"');
        }
      );
    },
  },
  {
    id: 'feat-meetings-1',
    name: 'Meetings page renders without crashing (mocked API)',
    run: async () => {
      const Meetings = require('../../Pages/Private/Settings/Meetings/Meetings').default;
      return await renderWithMockApi(
        withWrapper(<Meetings />, '/meetings'),
        async ({ container }) => {
          assert(container.firstChild !== null, 'Meetings page should render');
        }
      );
    },
  },
  {
    id: 'feat-bibliography-1',
    name: 'ArticleBibliography page renders (mocked API)',
    run: async () => {
      const ArticleBibliography = require('../../Pages/Private/Settings/Articles/ArticleBibliography').default;
      return await renderWithMockApi(
        withWrapper(<ArticleBibliography />, '/project/p1/bibliography'),
        async ({ container }) => {
          assert(container.firstChild !== null, 'ArticleBibliography should render');
        }
      );
    },
  },
  {
    id: 'feat-bibliography-2',
    name: 'SavedArticlesTable loading state renders spinner/indicator',
    run: async () => {
      const SavedArticlesTable = require('../../Components/ArticleBibliography/SavedArticlesTable').default;
      return await renderWithMockApi(
        withWrapper(
          <SavedArticlesTable
            articles={[]}
            loading={true}
            pagination={null}
            onPageChange={() => {}}
            onArticleDeleted={() => {}}
          />
        ),
        async ({ container }) => {
          assert(container.firstChild !== null, 'SavedArticlesTable should render in loading state');
        }
      );
    },
  },
  {
    id: 'feat-team-1',
    name: 'Team page renders without crashing (mocked API)',
    run: async () => {
      const Team = require('../../Pages/Private/Settings/Team/Team').default;
      return await renderWithMockApi(
        withWrapper(<Team />, '/team'),
        async ({ container }) => {
          assert(container.firstChild !== null, 'Team page should render');
        }
      );
    },
  },
  {
    id: 'feat-analysis-1',
    name: 'Analysis page renders without crashing (mocked API)',
    run: async () => {
      const Analysis = require('../../Pages/Private/Settings/Analysis/Analysis').default;
      return await renderWithMockApi(
        withWrapper(<Analysis />, '/analysis'),
        async ({ container }) => {
          assert(container.firstChild !== null, 'Analysis page should render');
        }
      );
    },
  },
  {
    id: 'feat-billing-1',
    name: 'PricingPage renders without crashing (mocked API)',
    run: async () => {
      const PricingPage = require('../../Pages/Private/Settings/Billing/PricingPage').default;
      return await renderWithMockApi(
        withWrapper(<PricingPage />, '/pricing'),
        async ({ container }) => {
          assert(container.firstChild !== null, 'PricingPage should render');
        }
      );
    },
  },
  {
    id: 'feat-questionnaire-1',
    name: 'QuestionRenderer handles text question type',
    run: async () => {
      const QuestionRenderer = require('../../Components/Questionnaire/QuestionRenderer').default;
      const textQuestion = { id: 1, type: 'text', text: 'What is your name?', required: true };
      const mockQuestionnaire = { theme_color: '#000000', theme_text_color: '#000000', theme_bg_color: '#ffffff' };
      return await renderWithMockApi(
        withWrapper(<QuestionRenderer question={textQuestion} questionnaire={mockQuestionnaire} mode="preview" />),
        async ({ container }) => {
          assert(container.firstChild !== null, 'QuestionRenderer should render for text type');
        }
      );
    },
  },
  {
    id: 'feat-researchpipeline-1',
    name: 'ResearchPipeline page renders without crashing (mocked API)',
    run: async () => {
      const ResearchPipeline = require('../../Pages/Private/Settings/ResearchPipeline/ResearchPipeline').default;
      return await renderWithMockApi(
        withWrapper(<ResearchPipeline />, '/research-pipeline'),
        async ({ container }) => {
          assert(container.firstChild !== null, 'ResearchPipeline should render');
        }
      );
    },
  },
  {
    id: 'feat-dashboard-1',
    name: 'Dashboard page renders without crashing (mocked API)',
    run: async () => {
      const Dashboard = require('../../Pages/Private/Dashboard').default;
      return await renderWithMockApi(
        withWrapper(<Dashboard />, '/dashboard'),
        async ({ container }) => {
          assert(container.firstChild !== null, 'Dashboard should render');
        },
        400
      );
    },
  },
];
